import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter, skip } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { User } from './../../../models/User';
import { AuthService } from './../../../shared/services/auth.service';
import { IntegrationTemplateAdminService } from './../../../shared/services/admin/integration-template-admin.service';
import {
  AccountVersion,
  AccountVersionService,
  NATIONAL,
} from './../../../shared/services/account-version.service';
import { AdminMenuSection, adminMenuSections } from './admin-menu-items';
import { MenuItem } from './menu-item';
import { MenuService } from './menu.service';
import { ADMINISTRATOR } from 'src/app/shared/services/user.service';
import { MenuNewBadgeService } from 'src/app/shared/services/menu-new-badge.service';
import { isNewBadgeExpired, isNewBadgeVisible, markNewBadgeSeen, NewBadge } from 'src/app/shared/utils/new-badge';
import { menuItemKey } from './menu-items';
import { WalletNavigationService } from 'src/app/shared/services/wallet-navigation.service';


@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy {

  get supportLink() {
    return environment.supportLink;
  }

  get baseUrl() {
    return environment.baseUrl;
  }

  get isAdministrator(): boolean {
    return this.user.profile_id === ADMINISTRATOR;
  }

  get canProposeIntegrations(): boolean {
    return this.isAdministrator || this.user?.can_propose_integrations === true;
  }

  /**
   * O indicador só faz sentido para quem tem as duas versões; conta sem acesso
   * ao internacional está sempre no Brasil e a bandeira seria ruído.
   *
   * "Sempre no Brasil" é invariante, não expectativa: sem `intl_enabled` o realm
   * é nacional (`AuthService.enforceRealmAccess`, CU-86akktm54). Por isso
   * esconder a bandeira aqui nunca esconde um estado internacional.
   */
  get canSwitchVersion(): boolean {
    return this.user?.intl_enabled === true;
  }

  layoutChanges: Observable<any>;

  version: AccountVersion = NATIONAL;

  title: string;
  menuItems: MenuItem[] = [];

  /**
   * O grupo Admin vem do catálogo, não do template. Ele não passa pelo
   * `MenuService`: é lista fixa, mostrada inteira para administrador e
   * escondida inteira para o resto.
   */
  adminSections: AdminMenuSection[] = adminMenuSections;

  isVisible: boolean;
  isSmallDevice: boolean;
  adminMenuVisible = false;
  openSubmenus: { [key: string]: boolean } = {};

  /** Badge "Plataformas" — total de plataformas aguardando aprovação (só admin). */
  pendingApprovalsCount = 0;

  expandedGroups = new Set<string>();

  user?: User;

  /**
   * Etiqueta "Novo" por item, recalculada ao carregar os itens e a cada
   * navegação — o template não lê o localStorage a cada ciclo de detecção.
   * A chave é o próprio item, não o `path`: grupo pode não ter rota própria.
   */
  newBadgeVisible = new Map<MenuItem, boolean>();

  /**
   * Quais itens têm a etiqueta, e até quando, por chave do item (`menuItemKey`).
   * Vem da config global (`MenuNewBadgeService`); até a resposta chegar — ou se a
   * API falhar — fica vazio e o menu aparece sem etiqueta nenhuma.
   */
  private newBadges = new Map<string, NewBadge>();

  /**
   * Telas abertas antes de a lista chegar (rede lenta): sem a lista, `markOpened` não
   * tem o que marcar, e a etiqueta de uma tela já aberta duraria mais uma visita.
   * `null` depois que a lista chegou.
   */
  private routesBeforeBadges: string[] | null = [];

  /**
   * Nenhuma das quatro fontes assinadas aqui termina sozinha (`getVersion` e
   * `getMenuVisibility` são BehaviorSubject, `userChanged` é EventEmitter e o
   * breakpoint observa para sempre), então sem `ngOnDestroy` um menu destruído
   * continuaria recarregando itens pelo realm de quem ainda está na tela.
   */
  private subscriptions = new Subscription();

  /** Multiusuários: o membro se apresenta ao suporte com o próprio nome, não o do dono. */
  get supportName(): string {
    return this.authService.currentMember?.name ?? this.user?.name;
  }

  constructor(
    private authService: AuthService,
    private menuService: MenuService,
    private breakpointObserver: BreakpointObserver,
    private integrationTemplateAdminService: IntegrationTemplateAdminService,
    private accountVersionService: AccountVersionService,
    private router: Router,
    private menuNewBadgeService: MenuNewBadgeService,
    private walletNavigationService: WalletNavigationService
  ) { }

  ngOnInit(): void {
    this.user = this.authService.getUser();

    // Os itens dependem do realm, mas o realm entra neles pelo USUÁRIO EFETIVO:
    // `MenuService.getMenuItems()` filtra por `globalActivated`, que é campo do
    // usuário. E toda troca de realm nasce de um `userChanged` — é dele, e só
    // dele, que o `AccountVersionService` republica (ver o `refresh()` pendurado
    // no construtor dele). Recarregar aqui TAMBÉM era recarregar duas vezes no
    // mesmo toggle, então esta assinatura fica com o que só ela dá: a bandeira
    // do indicador.
    this.subscriptions.add(this.accountVersionService
      .getVersion()
      .subscribe(version => this.version = version));

    if (!['app.smsconvert.com', 'app.smsfunnel.com.br'].includes(window.location.host)) {
      this.title = environment.title;
      window.document.title = environment.title + 'SMS Funnel';
    }

    this.initAdminExpansion();

    if (window.location.href.includes('call4u-audios') ||
        window.location.href.includes('broadcasts-call') ||
        window.location.href.includes('ligacoes-metricas')) {
      this.openSubmenus['/ligacoes'] = true;
    }

    this.subscriptions.add(this.menuService
      .getMenuVisibility()
      .subscribe(isVisible => this.isVisible = isVisible));

    this.layoutChanges = this.breakpointObserver
      .observe(['(max-width: 915px)']);

    this.subscriptions.add(this.layoutChanges.subscribe(result => {
      this.isSmallDevice = result.matches;
      // Quem decide o que abre em cada modo é o `MenuService`: no desktop ele
      // devolve a preferência salva, no celular fecha a gaveta. Forçar
      // `show()`/`hide()` aqui sobrescrevia a preferência em todo boot
      // (CU-86akkvmxc).
      this.menuService.setSmallDevice(this.isSmallDevice);
    }));

    // Fonte única da recarga: cobre a troca de realm, que sempre passa por aqui
    // antes de virar emissão de versão, e as trocas de usuário efetivo que não
    // mexem no realm — entrar e sair de simulação muda o filtro `simulated` dos
    // itens sem mexer na versão.
    this.subscriptions.add(this.authService.userChanged.subscribe(user => {
      this.loadMenuItems();
      this.user = user;
    }));

    // Badge "Plataformas" — só admin pode aprovar, então só admin assina.
    if (this.isAdministrator) {
      this.subscriptions.add(this.integrationTemplateAdminService.pendingApprovals$
        .subscribe((count) => this.pendingApprovalsCount = count));
      this.integrationTemplateAdminService.refreshPendingApprovals();
    }

    // `userChanged` só fala de mudança; a carga inicial é desta linha.
    this.loadMenuItems();

    // A lista da etiqueta "Novo" chega depois do primeiro desenho. Ao chegar, a
    // rota em que o painel já está conta como aberta: o menu nasce durante a
    // ativação da rota, antes do NavigationEnd.
    this.subscriptions.add(this.menuNewBadgeService.badges().subscribe(badges => {
      this.newBadges = new Map(badges.map(badge => [badge.key, badge] as [string, NewBadge]));
      (this.routesBeforeBadges || []).forEach(url => this.markOpened(url));
      this.routesBeforeBadges = null;
      this.markOpened(this.router.url);
    }));

    // "Abrir o item" é chegar na tela dele — pelo menu, por link ou pela URL —,
    // então a etiqueta "Novo" sai na navegação, não no clique.
    this.subscriptions.add(this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.routesBeforeBadges?.push(event.urlAfterRedirects);
        this.markOpened(event.urlAfterRedirects);
      }));

    // `GET /wallet/navigation` (M7, task 17tm466nz8b) chega DEPOIS da carga inicial feita
    // acima com o snapshot corrente (`WalletNavigationService.current`, replay síncrono de
    // um BehaviorSubject) — `skip(1)` ignora esse replay e só recarrega quando o valor muda
    // de verdade (resposta real chegando, login, entrar/sair de simulação).
    this.subscriptions.add(this.walletNavigationService.changes.pipe(skip(1)).subscribe(() => this.loadMenuItems()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadMenuItems(): void {
    this.menuItems = this.menuService.getMenuItems();
    this.initGroupExpansion();
    this.refreshNewBadges();
  }

  /**
   * Marca como vista a etiqueta "Novo" do item dono da rota aberta. A do grupo
   * (quando o admin marca o próprio grupo) sai quando o usuário abre qualquer filho.
   */
  markOpened(url: string): void {
    const route = (url || '').split(/[?#]/)[0];
    const opened: MenuItem[] = [];

    this.menuItems.forEach(item => {
      const children = (item.children || []).filter(child => this.routeBelongsTo(route, child.path));

      opened.push(...children);

      if (children.length || this.routeBelongsTo(route, item.path)) {
        opened.push(item);
      }
    });

    opened
      .map(item => this.badgeFor(item))
      // Etiqueta expirada não grava marca: ninguém mais a lê, e o logout a guardaria para sempre.
      .filter(badge => badge && !isNewBadgeExpired(badge))
      .forEach(badge => markNewBadgeSeen(badge, this.newBadgeUserId()));

    this.refreshNewBadges();
  }

  /** O grupo mostra "Novo" pela etiqueta dele ou enquanto algum filho ainda mostrar a sua. */
  private refreshNewBadges(): void {
    const now = new Date();
    const userId = this.newBadgeUserId();
    const visible = new Map<MenuItem, boolean>();

    this.menuItems.forEach(item => {
      (item.children || []).forEach(child => visible.set(child, isNewBadgeVisible(this.badgeFor(child), userId, now)));
      visible.set(item, isNewBadgeVisible(this.badgeFor(item), userId, now)
        || (item.children || []).some(child => visible.get(child)));
    });

    this.newBadgeVisible = visible;
  }

  private badgeFor(item: MenuItem): NewBadge | undefined {
    return this.newBadges.get(menuItemKey(item));
  }
  /**
   * A marca de "já vi" é do usuário efetivo (o simulado, numa simulação), lido
   * do `AuthService` e não de `this.user`: no `userChanged` os itens recarregam
   * antes de `this.user` ser trocado.
   */
  private newBadgeUserId(): string {
    return String(this.authService.getUser()?.id ?? '');
  }

  /**
   * O item é dono da rota quando ela é a dele ou uma sub-rota (`/wallet/x`), por
   * segmento: `/wallet-history` não é da Carteira.
   */
  private routeBelongsTo(route: string, path: string): boolean {
    return !!path && (route === path || route.startsWith(`${path}/`));
  }

  /** Open a group automatically when the current route lives inside one of its children. */
  private initGroupExpansion(): void {
    const url = window.location.href;
    this.menuItems
      .filter(item => item.children?.length)
      .forEach(group => {
        if (group.children.some(child => child.path && url.includes(child.path))) {
          this.expandedGroups.add(group.text);
        }
      });
  }

  toggleGroup(item: MenuItem): void {
    if (this.expandedGroups.has(item.text)) {
      this.expandedGroups.delete(item.text);
    } else {
      this.expandedGroups.add(item.text);
    }
  }

  isGroupExpanded(item: MenuItem): boolean {
    return this.expandedGroups.has(item.text);
  }

  /**
   * Estado inicial do grupo Admin, pela URL: o grupo abre quando a rota atual é
   * de admin, e dentro dele só a seção dona da rota fica aberta — é o que faz o
   * bloco certo estar à vista sem transformar o menu numa lista de 18 linhas de
   * novo. Fora de rota de admin não há seção "certa", então todas abrem.
   *
   * Mesma leitura de `initGroupExpansion()`, mas com call site próprio: aquela
   * roda de novo a cada `loadMenuItems()` (troca de realm, entrada e saída de
   * simulação), e reabrir seção que o usuário acabou de fechar seria um menu
   * que desfaz o que ele fez. Esta roda uma vez, no `ngOnInit`.
   *
   * O parâmetro existe para o teste: `window.location` não é sobrescrevível no
   * Karma, onde a URL é sempre a do runner.
   */
  initAdminExpansion(url: string = window.location.href): void {
    // Casamento por segmento, não por substring: com Provedores e o relatório
    // de provedores no mesmo grupo, `/provider-report` não pode abrir Operação.
    // O painel roteia por hash (`/#/admin-pricing/...`), então a rota vem do
    // hash quando ele existe.
    const parsed = new URL(url, 'http://localhost');
    const route = (parsed.hash.startsWith('#/') ? parsed.hash.slice(1) : parsed.pathname).split('?')[0];
    const current = this.adminSections.find(section =>
      section.items.some(item => this.routeBelongsTo(route, item.path)));

    this.adminMenuVisible = !!current;

    this.adminSections.forEach(section => {
      this.openSubmenus[section.key] = current ? section === current : true;
    });
  }

  /**
   * A cor da seção chega ao template como referência ao token, não como valor:
   * é assim que a versão internacional troca a cor sem passar pelo TypeScript.
   */
  sectionColor(section: AdminMenuSection): string {
    return `var(${section.colorToken})`;
  }

  /**
   * Seção aberta pelo usuário. A busca que forçava seções abertas saiu do Admin
   * em 07/10/2026: virou a busca global do header (`GlobalSearchComponent`),
   * para todos os usuários, com o mesmo Ctrl+K.
   */
  isSectionOpen(section: AdminMenuSection): boolean {
    return !!this.openSubmenus[section.key];
  }

  /** Destino absoluto de um item que mora fora do painel (Horizon). */
  externalUrl(externalPath: string): string {
    return `${this.baseUrl}${externalPath}`;
  }

  toggleMenu(): void {
    this.menuService.toggle();
  }

  /** No celular a gaveta fecha ao escolher um destino; no desktop nada muda. */
  closeOnSmallDevice(): void {
    if (this.isSmallDevice && this.isVisible) {
      this.menuService.hide();
    }
  }

  /** Esc fecha a gaveta do celular, como o clique no overlay. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeOnSmallDevice();
  }

  toggleAdminMenu(): void {
    this.adminMenuVisible = !this.adminMenuVisible;
  }

  toggleSubmenu(path: string): void {
    this.openSubmenus[path] = !this.openSubmenus[path];
  }
}
